import type { OdbNode } from "../../api/types";
import { insertAt, nodeAt, type Path, removeAt } from "./tree";

/** Glisser-déposer dans l'arbre de l'éditeur : calculs purs (cible, refus, déplacement). Une unité se dépose avant
 *  ou après une autre (sœur ou cousine), ou sur elle pour devenir son dernier subordonné ; jamais dans sa propre
 *  branche, ni sous une équipe. Le respect des échelons (R1) est vérifié ensuite par l'aperçu du serveur, qui
 *  signale l'erreur sur la ligne. Seuils du geste : ceux du glisser des listes vers la carte (4 px à la souris, 10 px
 *  au doigt, depuis la poignée). */

export type DropPosition = "before" | "after" | "inside";

export const DRAG_THRESHOLD = { mouse: 4, touch: 10 } as const;

/** `a` est `b` ou l'une de ses formations. */
export function isAncestorOrSelf(a: Path, b: Path): boolean {
  return a.length <= b.length && a.every((v, i) => b[i] === v);
}

/** Position du dépôt d'après la hauteur du pointeur dans la ligne : quart haut avant, quart bas après, milieu sur
 *  l'unité (si elle peut recevoir des subordonnés, sinon la moitié la plus proche). */
export function dropPosition(offsetY: number, height: number, canInside: boolean): DropPosition {
  const ratio = height > 0 ? offsetY / height : 0.5;
  if (canInside) return ratio < 0.25 ? "before" : ratio > 0.75 ? "after" : "inside";
  return ratio < 0.5 ? "before" : "after";
}

/** Le dépôt est-il permis ? `acceptsChildren` : l'unité cible peut recevoir des subordonnés (pas une équipe). */
export function canDrop(from: Path, target: Path, position: DropPosition, acceptsChildren: boolean): boolean {
  if (from.length === 0) return false; // la racine ne se déplace pas
  if (isAncestorOrSelf(from, target)) return false; // dans sa propre branche
  if (position === "inside") return acceptsChildren;
  return target.length > 0; // rien à côté de la racine
}

/** Chemin après le retrait de l'unité `removed` (les sœurs suivantes remontent d'un rang). */
export function shiftAfterRemoval(path: Path, removed: Path): Path {
  const depth = removed.length - 1;
  if (path.length <= depth) return path;
  const sameParent = removed.slice(0, depth).every((v, i) => path[i] === v);
  if (!sameParent || (path[depth] as number) <= (removed[depth] as number)) return path;
  return path.map((v, i) => (i === depth ? v - 1 : v));
}

/** Déplace l'unité `from` avant, après ou dans l'unité `target` ; rend l'arbre et le nouveau chemin de l'unité. */
export function moveNode(tree: OdbNode, from: Path, target: Path, position: DropPosition): { tree: OdbNode; path: Path } | null {
  const acceptsChildren = true; // vérifié par l'appelant (canDrop)
  if (!canDrop(from, target, position, acceptsChildren)) return null;
  const node = nodeAt(tree, from);
  const removed = removeAt(tree, from);
  const at = shiftAfterRemoval(target, from);
  if (position === "inside") {
    const size = nodeAt(removed, at).children.length;
    return { tree: insertAt(removed, at, size, node), path: [...at, size] };
  }
  const parent = at.slice(0, -1);
  const index = (at[at.length - 1] as number) + (position === "after" ? 1 : 0);
  return { tree: insertAt(removed, parent, index, node), path: [...parent, index] };
}
